import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Tag, { horizontalStyles, TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { PunchoutIdentityPageContext } from "@TKCCP/Pages/PunchoutIdentityPage";
import loadPunchoutIdentityList from "@TKCCP/Store/Data/PunchoutIdentity/Handlers/LoadPunchoutIdentity";
import clearSearch from "@TKCCP/Store/Pages/PunchoutIdentity/Handlers/ClearSearch";
import updateSearchFields from "@TKCCP/Store/Pages/PunchoutIdentity/Handlers/UpdateSearchFields";
import ApplicationStateTKCCP from "@TKCCP/Store/TKCCPState";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationStateTKCCP) => ({
    parameter: state.pagePunchoutIdentity.getPunchoutParameter,
});

const mapDispatchToProps = {
    updateSearchFields,
    clearSearch,
    loadPunchoutIdentityList,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SavedOrderListFilterStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    datesGridItem?: GridItemProps;
    datesContainer?: GridContainerProps;
    dateRangeFieldset?: InjectableCss;
    fromGridItem?: GridItemProps;
    fromDate?: DatePickerPresentationProps;
    toGridItem?: GridItemProps;
    toDate?: DatePickerPresentationProps;
    orderTotalGridItem?: GridItemProps;
    orderTotalContainer?: GridContainerProps;
    orderTotalFieldset?: InjectableCss;
    orderTotalOperatorGridItem?: GridItemProps;
    orderTotalOperatorSelect?: SelectPresentationProps;
    orderTotalAmountGridItem?: GridItemProps;
    orderTotalAmountTextField?: TextFieldPresentationProps;
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
    buttonsItem?: GridItemProps;
    clearFiltersButton?: ButtonPresentationProps;
}

export const savedOrderListFilterStyles: SavedOrderListFilterStyles = {
    container: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
            padding-top: 30px;
        `,
    },
    heading: {
        variant: "h5",
        css: css`
            margin: 0;
        `,
    },
    datesGridItem: {
        css: css`
            padding: 15px;
        `,
        width: [12, 12, 6, 4, 4],
    },
    datesContainer: { gap: 10 },
    dateRangeFieldset: {
        css: css`
            border: none;
            margin: 0;
            padding: 0;
            width: 100%;
        `,
    },
    fromGridItem: { width: 6 },
    toGridItem: { width: 6 },
    appliedFiltersContainer: { css: horizontalStyles },
    buttonsItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
            margin-bottom: 10px !important;
        `,
    },
    clearFiltersButton: {
        variant: "secondary",
    },
    toDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    orderTotalGridItem: {
        css: css`
            padding: 15px;
            margin-bottom: 10px;
        `,
        width: [12, 12, 8, 6, 6],
    },
    orderTotalContainer: {
        gap: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            > div {
                                width: calc(100% + 10px) !important;
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    orderTotalFieldset: {
        css: css`
            border: none;
            margin: 0;
            padding: 0;
            width: 100%;
        `,
    },
    orderTotalOperatorGridItem: { width: 6 },
    orderTotalAmountGridItem: { width: 6 },
    orderTotalAmountTextField: {
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 32px;
            `,
        },
    },
    fromDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
};

const StyledDateRangeFieldSet = getStyledWrapper("fieldset");
const StyledOrderTotalFieldSet = getStyledWrapper("fieldset");

export const styles = savedOrderListFilterStyles;
let updateTimeoutId: any;
const PunchoutIdentitySearchBox = ({ parameter, updateSearchFields, clearSearch }: Props) => {
    const [identitySearch, setidentitySearch] = React.useState("");
    const [identitySearchOperator, setidentitySearchOperator] = React.useState("");

    const identitySearchChangeHandler = (totalValue: string, totalOperator: string) => {
        if ((totalValue === "" && totalOperator !== "") || (totalValue !== "" && totalOperator === "")) {
            return;
        }

        updateTimeoutId = setTimeout(() => {
            updateSearchFields({
                identitySearch: totalValue,
                identitySearchOperator: totalOperator,
            });
        }, 250);
    };

    const identitySearchValueChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const total = event.target.value;
        setidentitySearch(total);

        updateTimeoutId = setTimeout(() => {
            identitySearchChangeHandler(total, identitySearchOperator);
        }, 250);
    };

    const identitySearchOperatorChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const operator = event.target.value;
        setidentitySearchOperator(operator);

        identitySearchChangeHandler(identitySearch, operator);
    };

    const resetidentitySearch = () => {
        setidentitySearch("");
        updateSearchFields({ identitySearchOperator: "", identitySearch: "" });
    };

    const clearFiltersClickHandler = () => {
        clearSearch();
        setidentitySearch("");
    };

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.headingGridItem}>
                <Typography {...styles.heading} as="h2">
                    {translate("Filter")}
                </Typography>
            </GridItem>
            <GridItem {...styles.orderTotalGridItem}>
                <StyledOrderTotalFieldSet {...styles.orderTotalFieldset}>
                    <GridContainer {...styles.orderTotalContainer}>
                        <GridItem {...styles.orderTotalOperatorGridItem}>
                            <Select
                                {...styles.orderTotalOperatorSelect}
                                label={translate("Search Identity")}
                                value={identitySearchOperator}
                                data-test-selector="identityList_comparisonMethod"
                                onChange={identitySearchOperatorChangeHandler}
                            >
                                <option value="">{translate("Select")}</option>
                                <option key="EmailAddress" value="EmailAddress">
                                    {translate("Email Address")}
                                </option>
                                <option key="User" value="User">
                                    {translate("User")}
                                </option>
                                <option key="CustomerNumber" value="CustomerNumber">
                                    {translate("Customer Number")}
                                </option>
                            </Select>
                        </GridItem>
                        <GridItem {...styles.orderTotalAmountGridItem}>
                            <TextField
                                {...styles.orderTotalAmountTextField}
                                value={identitySearch}
                                // type="number"
                                data-test-selector="identityList_comparisonValue"
                                onChange={identitySearchValueChangeHandler}
                            />
                        </GridItem>
                    </GridContainer>
                </StyledOrderTotalFieldSet>
            </GridItem>
            <GridItem {...styles.buttonsItem}>
                <Button
                    {...styles.clearFiltersButton}
                    data-test-selector="identityList_clearFiltersButton"
                    onClick={clearFiltersClickHandler}
                >
                    {translate("Clear Filters")}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PunchoutIdentitySearchBox),
    definition: {
        group: "Order History",
        displayName: "Punchout Identity Search Box",
        // allowedContexts: [PunchoutIdentityPageContext],
    },
};

export default widgetModule;
